import { useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import type {
  PersonStanding,
  CreateMediaTagRequest,
  PersonsListResponse,
  TaggedPerson,
} from "@family/shared";

interface TagPersonsModalProps {
  familySlug: string;
  mediaId: string;
  viewUrl: string;
  current: TaggedPerson[];
  open: boolean;
  onClose: () => void;
}

function shortName(givenName: string, familyName: string | null): string {
  if (!familyName) return givenName;
  return `${givenName} ${familyName[0]}.`;
}

export function TagPersonsModal({
  familySlug,
  mediaId,
  viewUrl,
  current,
  open,
  onClose,
}: TagPersonsModalProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const queryClient = useQueryClient();
  const [picker, setPicker] = useState<string>("");
  const [freeText, setFreeText] = useState("");
  const meQuery = useMe();

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug],
    queryFn: () =>
      apiFetch<PersonsListResponse>(`/families/${familySlug}/persons`),
    enabled: open,
  });

  const addTag = useMutation<TaggedPerson, Error, CreateMediaTagRequest>({
    mutationFn: (body) =>
      apiFetch<TaggedPerson>(
        `/families/${familySlug}/media/${mediaId}/tags`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      setPicker("");
      invalidateAll();
    },
  });

  // Create a new person then tag them.
  //
  // The standing is asked for here because this is the moment a person
  // record gets made. Tagging someone who already exists asks nothing — how
  // they are related is irrelevant to putting their name on a photo, and the
  // common path should not be taxed for the uncommon one.
  //
  // "Part of the family" deliberately leaves the standing unset rather than
  // writing "family": they have no relationships yet, so they belong in the
  // Tree audit for someone to connect properly. Saying they are family
  // without linking them would claim a place in a tree they are not in.
  const addAndTag = useMutation<TaggedPerson, Error, PersonStanding | null>({
    mutationFn: async (standing) => {
      const parts = freeText.trim().split(/\s+/);
      const givenName = parts[0]!;
      const familyName = parts.length > 1 ? parts.slice(1).join(" ") : undefined;
      const person = await apiFetch<{ id: string }>(
        `/families/${familySlug}/persons`,
        {
          method: "POST",
          body: JSON.stringify({ givenName, familyName, standing }),
        },
      );
      return apiFetch<TaggedPerson>(
        `/families/${familySlug}/media/${mediaId}/tags`,
        { method: "POST", body: JSON.stringify({ personId: person.id }) },
      );
    },
    onSuccess: () => {
      setFreeText("");
      invalidateAll();
      void queryClient.invalidateQueries({ queryKey: ["persons", familySlug] });
    },
  });

  const removeTag = useMutation<void, Error, string>({
    mutationFn: (tagId) =>
      apiFetch(`/families/${familySlug}/media/${mediaId}/tags/${tagId}`, {
        method: "DELETE",
      }),
    onSuccess: invalidateAll,
  });

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
    void queryClient.invalidateQueries({ queryKey: ["post"] });
    void queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === "person-media",
    });
  }

  const linkedPersonId = meQuery.data?.user.linkedPersonId ?? null;
  const alreadyTaggedIds = new Set(current.map((t) => t.id));

  const availablePersons =
    personsQuery.data?.persons.filter(
      (p) => !alreadyTaggedIds.has(p.id),
    ) ?? [];

  // Put the current user's linked person at the top
  const selfPerson = availablePersons.find((p) => p.id === linkedPersonId);
  const otherPersons = availablePersons.filter((p) => p.id !== linkedPersonId);
  const sortedPersons = selfPerson ? [selfPerson, ...otherPersons] : otherPersons;

  const selfAlreadyTagged = linkedPersonId ? alreadyTaggedIds.has(linkedPersonId) : true;

  return (
    <dialog
      ref={dialogRef}
      className="settings-dialog"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="settings-dialog__inner stack">
        <div className="row-between">
          <h2>Tag people</h2>
          <button
            type="button"
            className="btn-tertiary"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div
          style={{
            aspectRatio: "4 / 3",
            overflow: "hidden",
            borderRadius: 4,
            background: "var(--c-bg-alt)",
          }}
        >
          <img
            src={viewUrl}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              pointerEvents: "none",
            }}
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        </div>

        {/* Quick "Tag myself" button */}
        {linkedPersonId && !selfAlreadyTagged && (
          <button
            type="button"
            onClick={() => addTag.mutate({ personId: linkedPersonId })}
            disabled={addTag.isPending}
            style={{
              width: "100%",
              padding: "10px 16px",
              fontWeight: 600,
              fontSize: "0.95rem",
            }}
          >
            {addTag.isPending ? "Tagging…" : "🙋 Tag myself"}
          </button>
        )}

        <section className="stack">
          <h3 style={{ fontSize: "1rem" }}>Currently tagged</h3>
          {current.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              No one tagged yet.
            </p>
          ) : (
            <ul className="list-unstyled">
              {current.map((t) => (
                <li key={t.tagId} className="card">
                  <div className="row-between">
                    <span>
                      {shortName(t.givenName, t.familyName)}
                    </span>
                    <button
                      type="button"
                      className="btn-tertiary"
                      onClick={() => removeTag.mutate(t.tagId)}
                      disabled={removeTag.isPending}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="stack">
          <h3 style={{ fontSize: "1rem" }}>Add a tag</h3>
          {sortedPersons.length === 0 && !personsQuery.isLoading ? (
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              Everyone in the tree is already tagged.
            </p>
          ) : (
            <div
              style={{ display: "flex", gap: "var(--s-2)", alignItems: "end" }}
            >
              <div style={{ flex: 1 }}>
                <label htmlFor="tag-picker">Person from tree</label>
                <select
                  id="tag-picker"
                  className="select-skapa"
                  value={picker}
                  onChange={(e) => setPicker(e.target.value)}
                  disabled={addTag.isPending}
                >
                  <option value="">— Select —</option>
                  {sortedPersons.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.id === linkedPersonId ? `⭐ ${shortName(p.givenName, p.familyName)} (me)` : shortName(p.givenName, p.familyName)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() =>
                  picker && addTag.mutate({ personId: picker })
                }
                disabled={!picker || addTag.isPending}
              >
                {addTag.isPending ? "Adding…" : "Tag"}
              </button>
            </div>
          )}
        </section>

        {/* Free-text: tag someone not in the tree */}
        <section className="stack">
          <h3 style={{ fontSize: "1rem" }}>Tag someone new</h3>
          <p className="text-muted" style={{ fontSize: "0.85rem", margin: 0 }}>
            Nobody above? Type their name, then say who they are. This makes
            them a page of their own, so they can be written about either way.
          </p>
          <div style={{ flex: 1 }}>
            <label htmlFor="tag-freetext">Name</label>
            <input
              id="tag-freetext"
              type="text"
              placeholder="Full name"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              disabled={addAndTag.isPending}
              maxLength={200}
            />
          </div>
          {freeText.trim() && (
            <div style={{ display: "grid", gap: 6 }}>
              {(
                [
                  [null, "Part of the family", "Adds them and puts them in Tree audit so someone can connect them to the tree."],
                  ["friend", "Friend of the family", "Not a relative. Never counted as part of the tree."],
                  ["not_family", "Not family", "Appears in photos or stories only. Never counted as part of the tree."],
                ] as const
              ).map(([standing, label, hint]) => (
                <button
                  key={label}
                  type="button"
                  className={standing === null ? "" : "btn-secondary"}
                  onClick={() => addAndTag.mutate(standing)}
                  disabled={addAndTag.isPending}
                  style={{ textAlign: "left", display: "grid", gap: 2, padding: "8px 14px" }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {addAndTag.isPending ? "Adding…" : label}
                  </span>
                  {/* Inherit the button's own colour rather than
                      text-subtle: on the filled primary button a muted grey
                      drops below readable contrast. */}
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 400,
                      color: "inherit",
                      opacity: 0.75,
                    }}
                  >
                    {hint}
                  </span>
                </button>
              ))}
            </div>
          )}
          {addAndTag.error && (
            <p className="text-error">{addAndTag.error.message}</p>
          )}
        </section>

        {addTag.error && (
          <p className="text-error">{addTag.error.message}</p>
        )}
        {removeTag.error && (
          <p className="text-error">{removeTag.error.message}</p>
        )}
      </div>
    </dialog>
  );
}
